import { CategoryPreferences } from '@segment/consent-manager/types/types';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

import { getFeatureFlagsProperties } from '@pflegia/feature-flags/utils/helpers/get-feature-flag-properties';
import { SegmentConsentManagerBase } from '@pflegia/segment-ui/consent/SegmentConsentManagerBase';
import { setGoogleAndUetConsentModes } from '@pflegia/segment-ui/consent/set-google-and-uet-consent-mode';
import { PageView } from '@pflegia/segment-ui/types';

import { segmentService } from '@shared/application/analytics/segment.service';
import { IMPRINT_AND_PRIVACY, MAINTENANCE, PAGES_WITH_CUSTOM_TRACKING, TERMS_AND_CONDITIONS } from '@shared/application/constants';
import { decidePosthogCapture } from '@shared/posthog/decide-posthog-capture';
import { setupPostHogClient } from '@shared/posthog/get-posthog-client';
import { accessTokenService } from '@shared/utils/access-token.service';

interface Props {
  hideConsentModal?: boolean;
}

const SegmentConsentManager: React.FC<Props> = ({ hideConsentModal }) => {
  const { pathname, asPath } = useRouter();
  const landingAsPathRef = useRef(asPath);
  const landingPageViewRef = useRef<PageView | undefined>(undefined);
  const postAnalyticsHandledRef = useRef(false);
  const latestCookiesSettingsRef = useRef<CategoryPreferences | undefined>(undefined);

  useEffect(() => {
    const isCustomTrackedPage = PAGES_WITH_CUSTOM_TRACKING.some(page => landingAsPathRef.current.startsWith(page));
    if (isCustomTrackedPage) return;
    landingPageViewRef.current = segmentService.capturePageView(landingAsPathRef.current);
  }, []);

  const handleCookieSettings = useCallback((cookiesSettings: CategoryPreferences) => {
    latestCookiesSettingsRef.current = cookiesSettings;
    if (postAnalyticsHandledRef.current) return;
    postAnalyticsHandledRef.current = true;

    segmentService.ready(() => {
      const latestCookiesSettings = latestCookiesSettingsRef.current as CategoryPreferences;
      const landingPageView = landingPageViewRef.current;
      let featureFlagProperties: Record<string, unknown> = {};
      try {
        accessTokenService.identifyUser();
        const posthogClient = setupPostHogClient(true, latestCookiesSettings, { sendPageView: false });
        decidePosthogCapture(posthogClient, latestCookiesSettings, { sendPageView: false });
        if (landingPageView) featureFlagProperties = getFeatureFlagsProperties();
      } finally {
        segmentService.releasePendingEvents(
          landingPageView && { ...landingPageView, properties: { ...featureFlagProperties, ...landingPageView.properties } },
        );
      }

      const { advertising, marketingAndAnalytics } = latestCookiesSettings;
      if (advertising === false && marketingAndAnalytics === false) {
        setGoogleAndUetConsentModes([{ command: 'default', option: 'denied' }], { shouldSetUetConsent: Boolean(advertising) });
      }
      if (advertising || marketingAndAnalytics) {
        setGoogleAndUetConsentModes([{ command: 'update', option: 'granted' }], { shouldSetUetConsent: Boolean(advertising) });
      }
    });
  }, []);

  return pathname.includes(MAINTENANCE) ? null : (
    <SegmentConsentManagerBase
      onAnalyticsReady={handleCookieSettings}
      termsAndConditions={TERMS_AND_CONDITIONS}
      imprintAndPrivacy={IMPRINT_AND_PRIVACY}
      hideConsentModal={hideConsentModal}
    />
  );
};
export default SegmentConsentManager;
